import React from 'react';
import { cn } from '@components/ui/utils';

/**
 * Pill-in-gray-container tab strip used by drawers that split their
 * content into a small number of top-level tabs (currently EditPricingDrawer
 * and AddAircraftManuallyDrawer).
 *
 * Wizard usage — pass `disabled: true` on any tab whose gating condition
 * isn't yet satisfied. Disabled tabs render greyed out and swallow clicks
 * so the parent can enforce "must complete this step before moving on"
 * without duplicating the guard everywhere.
 *
 * @param {Object} props
 * @param {Array<{ key: string, label: string, disabled?: boolean }>} props.tabs
 * @param {string} props.activeTab            — key of the currently-active tab
 * @param {Function} props.onTabChange        — (nextKey) => void
 * @param {string} [props.className]
 */
const TabStrip = ({ tabs, activeTab, onTabChange, className }) => {
  return (
    <div className={cn('px-6', className)}>
      <div className="flex gap-1 bg-gray-100 p-1 rounded-lg">
        {tabs.map((tab) => {
          const isActive = activeTab === tab.key;
          const isDisabled = !!tab.disabled && !isActive;
          return (
            <button
              key={tab.key}
              type="button"
              disabled={isDisabled}
              onClick={() => !isDisabled && onTabChange(tab.key)}
              className={cn(
                'flex-1 py-2 px-3 rounded-md text-sm font-medium transition-all',
                isActive && 'bg-white text-gray-900 shadow-sm',
                !isActive && !isDisabled && 'text-gray-500 hover:text-gray-700',
                isDisabled && 'text-gray-300 cursor-not-allowed',
              )}
            >
              {tab.label}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default TabStrip;
